import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, MessageCircle, ArrowRight } from "lucide-react";
import {
  BUYER_TYPES,
  VANILLA_TYPES,
  formatApiError,
  trackEvent,
  waLink,
} from "@/lib/vgt";
import { Reveal, SectionHeader } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  business_name: "",
  business_email: "",
  phone_whatsapp: "",
  country: "",
  buyer_type: "",
  vanilla_type: "",
  estimated_quantity: "",
  notes: "",
  vgt_ref_code: "", // honeypot
};

const inputCls =
  "w-full bg-white border border-[#E6DEC3] rounded-xl px-4 py-3 text-sm text-[#2C1A0C] placeholder:text-[#6E5B4F]/50 focus:outline-none focus:ring-2 focus:ring-[#2E4F3E]/40 focus:border-[#2E4F3E] transition";

const Field = ({ label, required = true, children, id }) => (
  <div>
    <label htmlFor={id} className="block font-mono text-[11px] tracking-[0.18em] uppercase text-[#5C3A21] mb-2">
      {label} {required && <span className="text-[#2E4F3E]">*</span>}
    </label>
    {children}
  </div>
);

export const LeadForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, vanilla_type: e.detail }));
    window.addEventListener("vgt:select-product", handler);
    return () => window.removeEventListener("vgt:select-product", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      trackEvent("generate_lead", { buyer_type: form.buyer_type, vanilla_type: form.vanilla_type });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const waMessage = `Hello VGT team, I just submitted a quotation request.\nName: ${form.full_name}\nCompany: ${form.business_name}\nProduct: ${form.vanilla_type}\nQuantity: ${form.estimated_quantity}\nCountry: ${form.country}`;

  return (
    <section id="quotation" data-testid="quotation-section" className="py-20 sm:py-28 px-4 sm:px-8 bg-[#F5EFE6]">
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          align="center"
          eyebrow="06 · Request a Quotation"
          title="Tell Us What You Need"
          sub="Share your product, quantity, and destination — our export team replies with pricing and shipping options. MOQ from 1 kg."
        />
        <Reveal>
          {submitted ? (
            <div
              data-testid="lead-confirmation"
              className="bg-[#FDFBF7] border border-[#E6DEC3] rounded-3xl p-8 sm:p-12 text-center shadow-xl"
            >
              <CheckCircle2 size={52} className="mx-auto text-[#2E4F3E]" />
              <h3 className="mt-5 font-serif font-bold text-2xl sm:text-3xl text-[#2C1A0C]">
                Quotation request received
              </h3>
              <p className="mt-3 text-sm sm:text-base text-[#6E5B4F] leading-relaxed max-w-md mx-auto">
                Thank you, {form.full_name.split(" ")[0]}. Our export team will reply to{" "}
                <span className="font-semibold text-[#2C1A0C]">{form.business_email}</span> with pricing and
                shipping options. For the fastest response, continue on WhatsApp now.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3.5">
                <a
                  data-testid="confirmation-whatsapp-button"
                  href={waLink(waMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "lead_confirmation" })}
                  className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1EAE52] text-white font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  <MessageCircle size={16} />
                  Continue on WhatsApp
                </a>
                <button
                  data-testid="confirmation-new-request-button"
                  onClick={() => {
                    setForm(INITIAL);
                    setSubmitted(false);
                  }}
                  className="inline-flex items-center justify-center gap-2 border border-[#E6DEC3] hover:border-[#2E4F3E] text-[#2C1A0C] font-semibold rounded-full px-7 py-3.5 text-sm transition-colors"
                >
                  Submit another request
                </button>
              </div>
            </div>
          ) : (
            <form
              data-testid="lead-form"
              onSubmit={onSubmit}
              autoComplete="off"
              className="bg-[#FDFBF7] border border-[#E6DEC3] rounded-3xl p-6 sm:p-10 shadow-xl"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" id="full_name">
                  <input id="full_name" data-testid="lead-input-full-name" required minLength={2} maxLength={120}
                    className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Jane Cooper" />
                </Field>
                <Field label="Company / Business Name" id="business_name">
                  <input id="business_name" data-testid="lead-input-business-name" required minLength={2} maxLength={160}
                    className={inputCls} value={form.business_name} onChange={set("business_name")} placeholder="Cooper Foods GmbH" />
                </Field>
                <Field label="Business Email" id="business_email">
                  <input id="business_email" type="email" data-testid="lead-input-email" required
                    className={inputCls} value={form.business_email} onChange={set("business_email")} placeholder="jane@company.com" />
                </Field>
                <Field label="WhatsApp / Phone (with country code)" id="phone_whatsapp">
                  <input id="phone_whatsapp" type="tel" data-testid="lead-input-phone" required minLength={6} maxLength={30}
                    className={inputCls} value={form.phone_whatsapp} onChange={set("phone_whatsapp")} placeholder="+49 170 0000000" />
                </Field>
                <Field label="Destination Country" id="country">
                  <input id="country" data-testid="lead-input-country" required minLength={2} maxLength={80}
                    className={inputCls} value={form.country} onChange={set("country")} placeholder="Germany" />
                </Field>
                <Field label="Buyer Category" id="buyer_type">
                  <select id="buyer_type" data-testid="lead-select-buyer-type" required
                    className={inputCls} value={form.buyer_type} onChange={set("buyer_type")}>
                    <option value="" disabled>Select category</option>
                    {BUYER_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Vanilla Product Required" id="vanilla_type">
                  <select id="vanilla_type" data-testid="lead-select-vanilla-type" required
                    className={inputCls} value={form.vanilla_type} onChange={set("vanilla_type")}>
                    <option value="" disabled>Select product</option>
                    {VANILLA_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Estimated Quantity" id="estimated_quantity">
                  <input id="estimated_quantity" data-testid="lead-input-quantity" required maxLength={40}
                    className={inputCls} value={form.estimated_quantity} onChange={set("estimated_quantity")} placeholder="e.g. 1 kg, 10 kg, 100 kg+" />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Technical Specifications / Delivery Notes" id="notes" required={false}>
                  <textarea id="notes" data-testid="lead-input-notes" rows={4} maxLength={2000}
                    className={inputCls} value={form.notes} onChange={set("notes")}
                    placeholder="Grade, moisture target, packaging preference, delivery timeline…" />
                </Field>
              </div>
              <input
                type="text"
                name="vgt_ref_code"
                tabIndex={-1}
                autoComplete="off"
                data-lpignore="true"
                data-form-type="other"
                value={form.vgt_ref_code}
                onChange={set("vgt_ref_code")}
                className="absolute -left-[9999px] opacity-0 h-0 w-0"
                aria-hidden="true"
              />
              <button
                data-testid="lead-submit-button"
                type="submit"
                disabled={loading}
                className="group mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-[#2E4F3E] hover:bg-[#1B3B2B] disabled:opacity-60 text-[#FDFBF7] font-semibold rounded-full px-8 py-4 text-base transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Request a Quotation
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="mt-4 text-center text-xs text-[#6E5B4F]">
                Prefer chat?{" "}
                <a
                  data-testid="form-whatsapp-link"
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("whatsapp_click", { location: "form_footer" })}
                  className="font-semibold text-[#2E4F3E] underline underline-offset-2"
                >
                  Message us on WhatsApp
                </a>{" "}
                instead.
              </p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
